import React, { useState } from 'react';
import {
  Box,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  LinearProgress,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Error as ErrorIcon,
  InsertDriveFile as FileIcon
} from '@mui/icons-material';
import { base_url } from '../config/config';
import axios from 'axios';

const InvoiceUpload = ({ onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  
  // Handle file selection
  const handleFileSelect = (e) => {
    if (e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      
      // Validate file type - both extension and mime type
      const validExcelExtensions = /\.(xlsx|xls)$/i;
      const validExcelMimeTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/octet-stream'
      ];
      
      const isValidExtension = validExcelExtensions.test(selectedFile.name);
      const isValidMimeType = validExcelMimeTypes.includes(selectedFile.type);
      
      if (!isValidExtension) {
        setError('Bitte wählen Sie eine Excel-Datei (.xlsx oder .xls)');
        return;
      }
      
      // Log file details for debugging
      console.log('File details:', {
        name: selectedFile.name,
        type: selectedFile.type,
        size: selectedFile.size
      });
      
      // Validate file size (10 MB limit)
      if (selectedFile.size > 10 * 1024 * 1024) {
        setError('Die Datei ist zu groß (max. 10 MB)');
        return;
      }
      
      setFile(selectedFile);
      setError('');
    }
  };
  
  // Handle file removal
  const handleRemoveFile = () => {
    setFile(null);
    setError('');
  };
  
  // Handle file upload
  const handleUpload = async () => {
    if (!file) {
      setError('Bitte wählen Sie zuerst eine Datei aus');
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    setError('');
    
    const formData = new FormData();
    formData.append('excelFile', file);
    
    // Log the form data being sent
    console.log('Uploading file:', file.name, 'type:', file.type);
    
    try {
      const response = await axios.post(
        `${base_url}/v1/uploadExcel`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          }
        }
      );
      
      // Log the response for debugging
      console.log('Upload response:', response.data);
      
      if (response.data.status === 1) {
        setResult({
          success: true,
          message: response.data.message,
          invoicesCreated: response.data.invoicesCreated || 0,
          customersCreated: response.data.customersCreated || 0,
          duplicates: response.data.duplicates || 0,
          errors: response.data.errors || []
        });
        
        // Call the success callback with the result
        if (onSuccess) {
          onSuccess({
            invoicesCreated: response.data.invoicesCreated || 0,
            customersCreated: response.data.customersCreated || 0
          });
        }
      } else {
        setError(response.data.message || 'Fehler beim Hochladen der Datei');
        setResult({
          success: false,
          message: response.data.message,
          errors: response.data.errors || []
        });
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      
      // Better error handling with more details
      const errorMessage = error.response?.data?.message || 'Fehler beim Hochladen der Datei';
      const statusCode = error.response?.status;
      
      console.log(`Upload error (${statusCode}):`, errorMessage);
      
      setError(errorMessage);
      setResult({
        success: false,
        message: errorMessage,
        errors: error.response?.data?.errors || [],
        statusCode: statusCode
      });
    } finally {
      setUploading(false);
    }
  };
  
  return (
    <>
      <DialogTitle>
        Excel-Datei importieren
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {result?.success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Import erfolgreich! {result.invoicesCreated} Rechnungen wurden erstellt.
            {result.duplicates > 0 && ` ${result.duplicates} Duplikate wurden übersprungen.`}
          </Alert>
        )}
        
        {!result && (
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              border: '2px dashed #ccc',
              borderRadius: 2,
              p: 4,
              mb: 3,
              backgroundColor: '#f9f9f9'
            }}
          >
            <input
              type="file"
              id="upload-invoice-excel"
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
              disabled={uploading}
            />
            
            {!file ? (
              <>
                <CloudUploadIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" align="center" gutterBottom>
                  Ziehen Sie Ihre Excel-Datei hierher oder klicken Sie zum Auswählen
                </Typography>
                <Typography variant="body2" align="center" color="text.secondary" gutterBottom>
                  Unterstützte Formate: .xlsx, .xls (max. 10 MB)
                </Typography>
                <Button
                  variant="contained"
                  component="label"
                  htmlFor="upload-invoice-excel"
                  sx={{ mt: 2 }}
                  disabled={uploading}
                >
                  Datei auswählen
                </Button>
              </>
            ) : (
              <Box sx={{ width: '100%' }}>
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <DescriptionIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                      <Box>
                        <Typography variant="subtitle1" noWrap sx={{ maxWidth: '240px' }}>
                          {file.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {(file.size / 1024).toFixed(2)} KB
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton 
                      color="error" 
                      onClick={handleRemoveFile}
                      disabled={uploading}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Paper>
                
                {uploading && (
                  <Box sx={{ width: '100%', mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Uploading... {uploadProgress}%
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={uploadProgress} 
                      sx={{ height: 10, borderRadius: 5 }}
                    />
                  </Box>
                )}
                
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleUpload}
                    disabled={uploading || !file}
                    startIcon={<CloudUploadIcon />}
                  >
                    {uploading ? 'Wird hochgeladen...' : 'Datei hochladen'}
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        )}
        
        {/* Upload guidelines */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Hinweise zum Excel-Import:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon>
                <FileIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Die Excel-Datei sollte die folgenden Spalten enthalten:" 
                secondary="Rechnungsnummer, Rechnungsdatum, Firmenname, Gesamtbetrag, Fälligkeitsdatum"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <FileIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Optionale Spalten:"
                secondary="Kundennummer, Nettobetrag, Mehrwertsteuer, Mehrwertsteuersatz, Bezahlt am, Zahlungsbedingungen, Anmerkungen, Zuordnung1, Zuordnung2"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="success" />
              </ListItemIcon>
              <ListItemText 
                primary="Kundeninformationen werden automatisch angelegt"
                secondary="Wenn ein Kunde mit dem gleichen Namen existiert, wird dieser verwendet"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <ErrorIcon color="error" />
              </ListItemIcon>
              <ListItemText 
                primary="Duplikate werden übersprungen"
                secondary="Rechnungen mit bereits existierenden Rechnungsnummern werden übersprungen"
              />
            </ListItem>
          </List>
        </Box>
        
        {/* Show results if available */}
        {result && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Import-Ergebnisse:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon color="success" />
                </ListItemIcon>
                <ListItemText 
                  primary="Erstellte Rechnungen"
                  secondary={result.invoicesCreated}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon color="success" />
                </ListItemIcon>
                <ListItemText 
                  primary="Erstellte/Aktualisierte Kunden"
                  secondary={result.customersCreated}
                />
              </ListItem>
              {result.duplicates > 0 && (
                <ListItem>
                  <ListItemIcon>
                    <ErrorIcon color="warning" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Übersprungene Duplikate"
                    secondary={result.duplicates}
                  />
                </ListItem>
              )}
            </List>
            
            {result.errors && result.errors.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" color="error" gutterBottom>
                  Fehler:
                </Typography>
                <List dense>
                  {result.errors.map((error, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <ErrorIcon color="error" />
                      </ListItemIcon>
                      <ListItemText primary={error} />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button 
          onClick={onClose} 
          startIcon={<CancelIcon />}
        >
          Schließen
        </Button>
        {!result && !uploading && (
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleUpload}
            disabled={uploading || !file}
            startIcon={<CloudUploadIcon />}
          >
            Hochladen
          </Button>
        )}
      </DialogActions>
    </>
  );
};

export default InvoiceUpload;